<template>
  <div>
    <div class="header-space filter-line">
      <div class="filter-line__buttons">
        <div
          v-if="!roleTest"
          class="fs22"
        >
          {{ $t("auCapitals.projects.title") }}
        </div>
        <AuButton
          v-if="roleTest"
          center
          class="switches-indent"
          :type="projectType == 'common' ? 'primary' : 'secondary'"
          @click="handleButtonClick('common', $event)"
        >
          {{ $t("auCapitals.projects.typeProject") }}
        </AuButton>
        <AuButton
          v-if="roleTest"
          class="switches-indent"
          center
          :type="
            projectType.includes('pool') || projectType.includes('foundation')
              ? 'primary'
              : 'secondary'
          "
          @click="handleButtonClick('pool,foundation', $event)"
        >
          {{ $t("auCapitals.projects.typePool") }}
        </AuButton>
        <AuButton
          v-if="roleTest"
          center
          :type="projectType == 'stakingPool' ? 'primary' : 'secondary'"
          @click="handleButtonClick('stakingPool', $event)"
        >
          {{ $t("auCapitals.pools.stakingPools") }}
        </AuButton>
      </div>
      <div class="search-block">
        <AuInput
          v-model="search"
          icon="search"
          class="switches-indent"
          border-radius="22"
          :placeholder="$t('auCapitals.projects.placeholderSearch')"
        />
        <AuButton
          type="primary"
          width="130px"
          @click="showFilters = !showFilters"
        >
          <div class="filter-btn">
            {{ $t("auCapitals.projects.filter") }}
            <img
              src="/img/capital/grid.svg"
              alt="gird"
            >
          </div>
        </AuButton>
      </div>
    </div>
    <div
      v-if="showFilters"
      class="d-xl-flex filter-line"
    >
      <div class="col-xl d-flex switches">
        <AuSelect
          class="select-menu"
          :value="status"
          :options="typeOptions"
          :placeholder="$t('auCapitals.projects.placeholderFilter')"
          border-radius="22"
          @input="(value) => (status = value)"
        />

        <AuSelect
          class="selector select-menu"
          :value="selectedCategoryId"
          :placeholder="$t('auCapitals.projects.placeholderCategory')"
          :options="sectionCapitalOptions"
          border-radius="22"
          @input="(value) => (selectedCategoryId = value)"
        />
      </div>
    </div>

    <ProgressLoader
      v-if="loadingFilters"
      mode="indeterminate"
      color="#5AC396"
      size="150px"
    />


    <template v-if="!loadingFilters">
      <div v-if="!visibleProjectrs?.length">
        <div class="no_data">
          <span>{{ $t("auCapitals.projects.noData") }}<span
            class="click"
            @click="handleButtonClick('common', $event)"
          >{{ $t("auCapitals.projects.noDataReset") }}</span></span>
        </div>
      </div>
      <div
        v-if="projectType == 'stakingPool'"
        class="projects pool-projects"
      >
        <Pool
          v-for="proj in visibleProjectrs"
          :key="proj.id"
          :project="proj"
          :active-project-id="projectIdFromQuery"
        />
      </div>
      <div
        v-else
        class="projects"
      >
        <Component
          :is="capitalCard"
          v-for="proj in visibleProjectrs"
          :key="proj.id"
          :project="proj"
        />
      </div>
    </template>

    <div class="load-more">
      <AuButton
        v-if="currentTake < totalItems && !loadingFilters"
        type="primary"
        center
        width="125px"
        :is-progress="loading"
        @click="getMore"
      >
        {{ $t("auCapitals.projects.loadMore") }}
      </AuButton>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import CapitalCard from "@/components/capital/CapitalCard.vue";
import CapitalCardRWA from "@/components/capital/CapitalCardRWA.vue";
import Pool from "@/components/capital/Pool.vue";
import ProgressLoader from "@/loaders/progress-bar";
import { checkAccess, scope } from "@/help/accessData";
import { convertCapital } from "@/components/capital/capitalHelper.js";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "Projects",
  components: {
    Pool,
    CapitalCard,
    ProgressLoader
  },
  data() {
    return {
      search: null,
      projects: [],
      filtersFlag: {},
      selectedCategoryId: null,
      auCapitals: [],
      page: 1,
      totalItems: 1,
      loading: true,
      loadingFilters: false,
      status: "All",
      currentTake: 32,
      take: 32,
      projectType: "common", //common, pool,  stakingPool
      showFilters: false
    };
  },

  computed: {
    ...mapGetters({
      sectionCapital: "sectionCapital",
      searchedAuCapitals: "capitals/searchedAuCapitals",
      companies: "companies",
      followings: "capitals/followings",
      isTestRole: "profile/isTestRole",
      profile: "profile/profile"
    }),

    capitalCard() {
      return this.isNotRwa ? CapitalCard : CapitalCardRWA;
    },

    isNotRwa() {
      return checkAccess(scope.notRwa);
    },

    projectIdFromQuery() {
      return this.$route.query?.projectId;
    },

    title() {
      const ret = process.env.VUE_APP_TITLE ?? " ";
      return ret[0].toUpperCase() + ret.toLowerCase().slice(1);
    },

    typeOptions() {
      return [
        { display: this.$t("auCapitals.projects.typeAll"), value: "All" },
        { display: "Open", value: "Open" },
        { display: "Waiting", value: "Waiting" },
        { display: "Closed", value: "Closed" },
        { display: "Completed", value: "Completed" },
        { display: "Declined", value: "Declined" }
      ];
    },

    sectionCapitalOptions() {
      const arrList = _.map(this.sectionCapital, (capital) => ({
        display: capital.name,
        value: capital.id
      }));

      arrList.unshift({
        display: "all",
        value: -1
      });

      return arrList;
    },

    roleTest() {
      return checkAccess(scope.testRole);
    },

    visibleProjectrs() {
      return _.filter(this.projects, (x) => x.visibility || this.isTestRole);
    }
  },

  watch: {
    "$route.query": {
      immediate: true,
      async handler(newQuery, oldQuery) {
        this.loadingFilters = true;

        const keysToCompare = [
          "status",
          "isFollowed",
          "sectionId",
          "applicationType"
        ];
        const hasMeaningfulChange = keysToCompare.some(
          (key) => newQuery[key] !== oldQuery?.[key]
        );

        // обновляем фильтры всегда
        this.status = newQuery?.status || "All";
        this.selectedCategoryId =
          newQuery?.sectionId === -1 ? null : newQuery?.sectionId;
        this.projectType = newQuery?.applicationType || "common";

        if (hasMeaningfulChange || !oldQuery) {
          // делаем запрос только если что-то кроме projectId изменилось
          await this.getCapital();
        }

        this.loadingFilters = false;
      }
    },

    search: _.debounce(async function () {
      this.loadingFilters = true;
      await this.getCapital();
      this.loadingFilters = false;
    }, 500),

    async status() {
      this.loadingFilters = true;
      await this.getCapital();
      this.loadingFilters = false;
    },

    async selectedCategoryId() {
      this.loadingFilters = true;
      await this.getCapital();
      this.loadingFilters = false;
    },

    async projectType(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadingFilters = true;
        await this.updateQueryParams();
        await this.getCapital();
        this.loadingFilters = false;
      }
    }
  },

  async mounted() {
    await this.getSectionCapital();
    await this.getCapital();
    this.loading = false;
    for (let i = 0; i < this.sectionCapital; ++i) {
      let item = {};
      item[this.sectionCapital[i].id] = false;
      this.filtersFlag.push(item);
    }
  },

  methods: {
    ...mapActions({
      getSectionCapital: "getSectionCapital",
      getAllAuCapitalsPagination: "capitals/getAllAuCapitalsPagination",
      getStakingPoolsEarned: "capitals/getStakingPoolsEarned",
      sendCapital: "sendCapital"
    }),

    handleButtonClick(type, event) {
      const isCtrlClick = event?.ctrlKey || event?.metaKey;

      const newQuery = {
        ...this.$route.query,
        applicationType: type
      };

      if (type === "common" || type === "pool") {
        delete newQuery.projectId;
      }

      const target = {
        path: this.$route.path,
        query: newQuery
      };

      if (isCtrlClick) {
        const resolved = this.$router.resolve(target);
        window.open(resolved.href, "_blank");
      }
      else {
        // Сначала обновляем URL и потом меняем состояние иначе не работает
        this.$router.replace(target).then(() => {
          this.projectType = type;
        });
      }
    },

    async updateQueryParams() {
      const newQuery = {
        ...this.$route.query,
        applicationType: this.projectType
      };
      await this.$router.replace({ query: newQuery });
    },

    updateFilters() {
      const newQuery = { ...this.$route.query };

      if (this.status && this.status !== "All") {
        newQuery.status = this.status;
      }
      else {
        delete newQuery.status;
      }

      if (this.selectedCategoryId !== -1) {
        newQuery.sectionId = this.selectedCategoryId;
      }
      else {
        delete newQuery.sectionId;
      }

      if (this.projectType && this.projectType !== "common") {
        newQuery.applicationType = this.projectType;
      }
      else {
        delete newQuery.applicationType;
      }

      this.$router.replace({ query: newQuery });
    },

    async getMore() {
      this.loading = true;
      this.currentTake += this.take;
      await this.getCapital();
      this.loading = false;

      const loadMore = document.querySelector(".main-panel");
      const projects = document.querySelector(".projects");

      loadMore.scrollTop = projects.clientHeight;
    },

    async pageChanged(actualPage) {
      this.page = actualPage;
      await this.getCapital();
    },

    async getCapital() {
      try {
        let statusFilter = this.status.toLowerCase();

        if (this.status == "All") {
          statusFilter = null;
        }
        else if (this.status == "Declined") {
          statusFilter = "not_success";
        }
        else if (this.status == "Completed") {
          statusFilter = "success";
        }
        this.updateFilters();

        let resp = await this.getAllAuCapitalsPagination({
          page: 1,
          itemsPerPage: this.currentTake,
          order: "desc",
          description: this.search,
          status: statusFilter,
          sectionId:
            this.selectedCategoryId === -1 ? null : this.selectedCategoryId,
          applicationType: this.projectType
        });
        this.auCapitals = resp.data;
        this.totalItems = resp.totalItems;

        const capitalData = this.auCapitals;
        this.projects = await this.convertAuCapitals(capitalData);

        if (this.projectType == "stakingPool") {
          const earned = await this.getStakingPoolsEarned();
          this.projects.forEach((project) => {
            const projectEarned = earned.find(
              (earnedItem) => earnedItem.event === project.id
            );
            if (projectEarned) {
              project.stake = projectEarned;
            }
          });
        }
      }
      catch (e) {
        console.log("error", e);
      }
    },

    selectedSearched() {
      const set = new Set();
      for (const { id } of this.searchedAuCapitals) {
        set.add(id);
      }

      const intersection = this.projects.filter(({ id }) => set.has(id));
      return intersection;
    },

    async convertAuCapitals(auCapitals) {
      let projects = [];
      for (const item of auCapitals) {
        const project = convertCapital(item, this.sectionCapital);
        projects.push(project);
      }

      return projects;
    }
  }
};
</script>

<style lang="scss" scoped>
.header-space {
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 1010px) {
    flex-direction: column;
    gap: 20px;
  }

  .search-block {
    display: flex;
    justify-content: space-between;

    @media screen and (max-width: 800px) {
      padding-right: 15px;
    }
  }
}
.switches-indent {
  margin-right: 10px;
}
.switches {
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.filter-line {
  margin-top: 25px;
  margin-bottom: 15px;

  @media screen and (max-width: 1199px) {
    margin-bottom: 25px;
  }

  &__buttons {
    display: flex;
    align-items: center;

    @media screen and (max-width: 600px) {
      align-items: start;
      flex-direction: column;
      width: 100%;
      gap: 10px;

      > button {
        width: 100% !important;
      }
    }
  }
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
}

.projects {
  display: flex;
  gap: 25px;
  flex-wrap: wrap;
}

.pool-projects {
  gap: 15px;
}

.selector {
  min-width: 220px;
}

.following {
  display: flex;
  gap: 5px;
}

.load-more {
  display: flex;
  justify-content: center;

  > button {
    padding: 20px;
    margin-top: 20px;
  }
}
.no_data {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  color: var(--color-50);
}
.click {
  cursor: pointer;
  text-decoration: underline;
}
</style>
